// The candidate's own view of a language test.
//
// One page for both papers, because from the candidate's side they are the same
// experience: read the brief, answer every question, submit once. What differs
// is what the result *means*, and that is the only thing branched on — a
// placement paper reports a CEFR level and never says "passed" or "failed",
// because being placed at A1 is information, not a failure.
//
// Nothing here ever receives the answer key: the API sends prompts and options
// only, and marking happens on the server.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Award, CheckCircle2, ChevronLeft, ChevronRight, Clock3, GraduationCap, ShieldCheck, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { getPublicLanguageTest, submitPublicLanguageTest } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

function Shell({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F8FA] p-6">
      <Card className="w-full max-w-xl border-[#062B1A]/10 shadow-lg">
        <CardContent className="p-8 text-center">{children}</CardContent>
      </Card>
    </div>
  );
}

export default function LanguageTest() {
  const { token } = useParams();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getPublicLanguageTest(token)
      .then(({ data }) => {
        setTest(data);
        if (data.status === 'submitted') setResult(data);
      })
      .catch((error) => toast.error(
        error?.response?.data?.detail || 'This test link could not be opened',
      ))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] p-6">
        <div className="mx-auto h-96 max-w-3xl animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }

  if (!test) {
    return (
      <Shell>
        <XCircle className="mx-auto mb-4 size-12 text-[#062B1A]/30" />
        <h1 className="text-2xl font-semibold text-[#062B1A]">Test not found</h1>
        <p className="mt-3 text-[#062B1A]/60">
          Please check the link in your invitation email, or contact Admissions.
        </p>
      </Shell>
    );
  }

  if (test.status === 'expired') {
    return (
      <Shell>
        <XCircle className="mx-auto mb-4 size-12 text-red-600" />
        <h1 className="text-2xl font-semibold text-[#062B1A]">This link has expired</h1>
        <p className="mt-3 text-[#062B1A]/60">
          Please contact Admissions to request a new link.
        </p>
      </Shell>
    );
  }

  const placement = (result?.scoring || test.scoring) === 'placement';

  if (result) {
    const passed = !!result.passed;
    return (
      <Shell>
        {placement
          ? <GraduationCap className="mx-auto size-16 text-[#062B1A]" />
          : passed
            ? <CheckCircle2 className="mx-auto size-16 text-emerald-600" />
            : <XCircle className="mx-auto size-16 text-amber-600" />}
        <h1
          className="mt-5 text-3xl font-semibold text-[#062B1A]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {placement ? 'Test completed' : passed ? 'Assessment passed' : 'Assessment completed'}
        </h1>

        {placement ? (
          <>
            <p className="mt-3 text-[#062B1A]/65">
              You scored <strong>{result.score}</strong> out of {result.max_score}.
            </p>
            <div className="mt-6 rounded-xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8A6508]">
                Your level
              </p>
              <p
                className="mt-1 text-4xl font-semibold text-[#062B1A]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                {result.level_label || '—'}
              </p>
              {result.level_description && (
                <p className="mt-2 text-sm text-[#062B1A]/70">{result.level_description}</p>
              )}
            </div>
            <div className="mt-5 rounded-xl bg-[#062B1A]/[0.04] p-4 text-sm text-[#062B1A]/75">
              Admissions has received your result and will use this level to place you in the
              right class. A copy has been emailed to you.
            </div>
          </>
        ) : (
          <>
            <p className="mt-3 text-[#062B1A]/65">
              You scored <strong>{result.percentage}%</strong> ({result.score}/{result.max_score}).
              The passing grade is {result.pass_percentage}%.
            </p>
            <div className={`mt-6 rounded-xl p-4 text-sm ${
              passed ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
            }`}>
              {passed
                ? 'Congratulations. Admissions has received your passing result, and a copy has been emailed to you.'
                : 'Admissions has received your result and will contact you about next steps. A copy has been emailed to you.'}
            </div>
          </>
        )}
      </Shell>
    );
  }

  const questions = test.questions || [];
  const question = questions[index];
  const answered = Object.keys(answers).length;

  if (!started) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] px-4 py-10">
        <Card className="mx-auto max-w-2xl border-[#062B1A]/10 shadow-lg">
          <CardContent className="p-6 sm:p-10">
            <div className="mb-6 flex size-14 items-center justify-center rounded-2xl bg-[#062B1A]">
              <Award className="size-7 text-[#D4AF37]" />
            </div>
            <h1
              className="text-3xl font-semibold text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {test.test_label}
            </h1>
            <p className="mt-3 text-[#062B1A]/65">
              Hello {test.recipient_name}. Please read these instructions carefully before beginning.
            </p>
            <div className="mt-6 space-y-3 rounded-xl border border-[#062B1A]/10 bg-white p-5 text-sm text-[#062B1A]/75">
              <p>
                <strong>Questions:</strong>{' '}
                {placement
                  ? `${questions.length} multiple-choice questions covering A1 to B2. They get harder as you go — answer as many as you can.`
                  : `${questions.length} multiple-choice questions selected from our question bank.`}
              </p>
              <p>
                <strong>Suggested time:</strong> {test.duration_minutes} minutes.
                Choose the best answer for every question.
              </p>
              <p>
                <strong>{placement ? 'Result:' : 'Passing grade:'}</strong>{' '}
                {placement
                  ? 'There is no pass mark. Your total places you on the CEFR scale so we can put you in the right class.'
                  : `${test.pass_percentage}% or higher.`}
              </p>
              <p>
                <strong>Submission:</strong> You may submit this secure attempt once. Your result is
                calculated automatically, emailed to you, and sent to Admissions.
              </p>
            </div>
            <div className="mt-5 flex items-start gap-3 rounded-xl bg-[#D4AF37]/10 p-4 text-sm text-[#062B1A]">
              <ShieldCheck className="mt-0.5 size-5 shrink-0 text-[#9A7818]" />
              <p>
                Complete the test independently, without a dictionary or a translator. Do not close
                the page until your result has been submitted.
              </p>
            </div>
            <Button
              onClick={() => setStarted(true)}
              className="mt-7 w-full bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              I understand — begin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const submit = async () => {
    if (answered !== questions.length) {
      toast.error(`Please answer all questions. ${questions.length - answered} remaining.`);
      return;
    }
    if (!window.confirm('Submit your final answers? You cannot change them afterward.')) return;
    try {
      setSubmitting(true);
      const { data } = await submitPublicLanguageTest(token, answers);
      setResult(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Your answers could not be submitted');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] px-4 py-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex flex-col gap-3 rounded-xl bg-[#062B1A] p-4 text-white sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">{test.test_label}</p>
            <p className="text-xs text-white/65">Question {index + 1} of {questions.length}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-white/10 text-white">
              <Clock3 className="mr-1 size-3.5" />{test.duration_minutes} min suggested
            </Badge>
            <Badge className="bg-[#D4AF37] text-[#062B1A]">
              {answered}/{questions.length} answered
            </Badge>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1fr_240px]">
          <Card className="border-[#062B1A]/10 shadow-sm">
            <CardContent className="p-6">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {question?.skill && <Badge variant="outline">{question.skill}</Badge>}
                {/* The level is a useful orientation on the placement paper and
                    meaningless on the proficiency one, which sends none. */}
                {question?.level && <Badge variant="outline">{question.level}</Badge>}
              </div>

              {/* A reading question's passage. Kept visually distinct from the
                  question so nobody answers the passage by mistake. */}
              {question?.passage && (
                <blockquote className="mb-5 rounded-xl border-l-4 border-[#D4AF37] bg-[#062B1A]/[0.03] p-4 text-sm leading-relaxed text-[#062B1A]/80">
                  {question.passage}
                </blockquote>
              )}

              <h2 className="text-lg font-semibold leading-relaxed text-[#062B1A]">
                {question?.text}
              </h2>

              <RadioGroup
                value={answers[question?.id] === undefined ? '' : String(answers[question.id])}
                onValueChange={(value) => setAnswers((prev) => ({
                  ...prev, [question.id]: Number(value),
                }))}
                className="mt-6 space-y-3"
              >
                {question?.options.map((option, optionIndex) => (
                  <Label
                    key={optionIndex}
                    htmlFor={`${question.id}-${optionIndex}`}
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#062B1A]/10 p-4 hover:bg-[#062B1A]/[0.03]"
                  >
                    <RadioGroupItem
                      id={`${question.id}-${optionIndex}`}
                      value={String(optionIndex)}
                      className="mt-0.5"
                    />
                    <span className="font-normal leading-relaxed text-[#062B1A]/80">{option}</span>
                  </Label>
                ))}
              </RadioGroup>

              <div className="mt-7 flex justify-between">
                <Button variant="outline" disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>
                  <ChevronLeft className="mr-1 size-4" />Previous
                </Button>
                {index < questions.length - 1 ? (
                  <Button onClick={() => setIndex((i) => i + 1)} className="bg-[#062B1A] text-white">
                    Next<ChevronRight className="ml-1 size-4" />
                  </Button>
                ) : (
                  <Button
                    disabled={submitting} onClick={submit}
                    className="bg-[#D4AF37] text-[#062B1A] hover:bg-[#C29C2F]"
                  >
                    {submitting ? 'Submitting...' : 'Submit answers'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="h-fit border-[#062B1A]/10">
            <CardContent className="p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#062B1A]/50">
                Questions
              </p>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((item, itemIndex) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-label={`Go to question ${itemIndex + 1}`}
                    onClick={() => setIndex(itemIndex)}
                    className={`size-9 rounded-lg border text-xs font-semibold ${
                      itemIndex === index ? 'border-[#D4AF37] bg-[#D4AF37]/15'
                        : answers[item.id] !== undefined ? 'border-[#062B1A]/20 bg-[#062B1A]/5'
                          : 'border-[#062B1A]/10 bg-white'
                    }`}
                  >
                    {itemIndex + 1}
                  </button>
                ))}
              </div>
              <Button
                onClick={submit}
                disabled={submitting || answered !== questions.length}
                className="mt-5 w-full bg-[#062B1A] text-white"
              >
                Submit all answers
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
